<template>
  <div class="sharePicker__container">
    <template v-if="getSelfContactListPending">
      <v-skeleton-loader type="paragraph@3" />
    </template>
    <template v-else>
      <MembersAutocomplete class="sharePicker__search"
                           :items="computedContactList"
                           :placeholder="$t('common.subscription.membersPicker.autocompletePlaceholder')"
                           @select="onSelectUser"
      />

      <p class="text--small text--bold">{{ $t('common.subscription.membersPicker.title')}}</p>
      <p class="text--small">{{ $t('common.subscription.membersPicker.subtitle')}}</p>

      <div class="sharePicker__items">
        <AppSubscriptionMembersPickerCard
          v-for="user in computedContactList"
          :key="`user-${user.id}`"
          v-model="members"
          :member-id="user.id"
          @update:model-value="onWithUsersChange"
        >
          {{ user.fullName }}
        </AppSubscriptionMembersPickerCard>
      </div>
    </template>
  </div>
</template>

<script lang='ts'>
import { defineComponent, PropType } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'

import AppSubscriptionMembersPickerCard from '@/common/subscription-members-picker/AppSubscriptionMembersPickerCard.vue'
import { toast } from '@/common/utils/toast'
import MembersAutocomplete from '@/project/members/MembersAutocomplete.vue'

type DataType = {
  membersValue: number[]
}

export default defineComponent({
  name: 'SubscriptionMembersPicker',
  components: { AppSubscriptionMembersPickerCard, MembersAutocomplete },
  props: {
    selectedMembers: {
      type: Array as PropType<number[]>,
      required: true,
    },
  },
  data (): DataType {
    return {
      membersValue: [],
    }
  },
  computed: {
    ...mapState('user', ['selfContactListFromSubscription', 'getSelfContactListPending']),
    ...mapGetters('user', ['currentUserId']),
    computedContactList () : Array<{ id: number, fullName: string }> {
      return this.selfContactListFromSubscription.filter(
        (contact: ObjectWith<{ id: number }>) => contact.id !== this.currentUserId,
      ) || []
    },
    members: {
      get () : Array<number> {
        if (this.membersValue.length) {
          return this.membersValue
        } else {
          return this.selectedMembers
        }
      },
      set (val: Array<number>) {
        this.membersValue = val
      },
    },
  },
  mounted () {
    this.prepareGetSubscriptionUsers()
  },
  methods: {
    ...mapActions('user', ['GET_SELF_CONTACT_LIST']),
    async prepareGetSubscriptionUsers () {
      try {
        await this.GET_SELF_CONTACT_LIST({ fromUserSubscriptionOnly: true })
      } catch (error) {
        toast.error(this.$t('common.subscription.errors.getSubscriptionUsersError'))
      }
    },
    onSelectUser (member: ObjectWith<{ id: number }>) {
      const memberIsNotCurrentlySelected = this.members.every(id => id !== member.id)

      if (memberIsNotCurrentlySelected) {
        this.membersValue = [...this.membersValue, member.id]
        this.$emit('update-members-selection', this.membersValue)
      }
    },
    onWithUsersChange (members: Array<number> | null) {
      this.$emit('update-members-selection', members)
    },
  },
})
</script>

<style lang="scss" scoped>
.sharePicker__container {
  margin-top: 16px;
}
.sharePicker__items,
.sharePicker__search,
.v-skeleton-loader {
  display: grid;
  gap: 24px;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
}
:deep( .v-input__prepend-outer) {
  margin: 0;
  position: absolute;
}
.sharePicker__search {
  margin-top: 24px;
}

.sharePicker__items {
  margin-top: 24px;
}

.sharePicker__item {
  justify-self: stretch;
  padding: 10px 14px;
  border: 1px solid #DEE2E6;
  border-radius: 4px;
  margin: 0;
  transition: background-color .4s ease-in-out;
}

:deep(.sharePicker__item .v-input--checkbox) {
  margin-top: 0;
}
</style>
